﻿import { DI } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { eBundle } from "@app/helpers/dirs";
import { StateHelper } from "@app/helpers/state.helper";
import "@app/index"; // make sure app.js is the last thing in the bundle
import { AuthService } from "@app/services/auth.service";
import { BundleLoaderService } from "@app/services/bundle-loader.service";
import { TelemetryService } from "@app/services/telemetry.service";
import { ThemeService } from "@app/services/theme.service";
import { eEnvironment, eViewType, IProductTranslation, IRootScope, ITranslation } from "@models";
import { icons, Utils } from "@tools";
import { StateProvider, StateService, Transition, TransitionService, UrlRouterProvider } from "@uirouter/angularjs";
import * as angular from "angular";
import { ApiService } from "./services/api.service";
import { QuoteService } from "./services/quote.service";
import { SfdcService } from "./services/sfdc.service";
export * from "@app/index";

angular.module('appTemplates', []);

export let kbapp: ng.IModule = angular.module("kbapp",
    [
        "ngAnimate",
        "ngCookies",
        "ngSanitize",
        "ui.router",
        "ajoslin.promise-tracker",
        "oc.lazyLoad",
        "appTemplates",
        "text-mask"
        // "jcs.angular-http-batch"
    ]);

DI.fillAngularApp(kbapp);

kbapp.config([
    "$locationProvider",
    "$stateProvider",
    "$httpProvider",
    "$urlRouterProvider",
    "$provide",
    "$animateProvider",
    // "httpBatchConfigProvider",
    "$compileProvider",
    (
        $locationProvider: ng.ILocationProvider,
        $stateProvider: StateProvider,
        $httpProvider: ng.IHttpProvider,
        $urlRouterProvider: UrlRouterProvider,
        $provide: ng.auto.IProvideService,
        $animateProvider: ng.animate.IAnimateProvider,
        // httpBatchConfigProvider: any,
        $compileProvider: ng.ICompileProvider
    ) => {
        // don't use hashbang mode
        $locationProvider.html5Mode(true);

        // Remove the header used to identify ajax call  that would prevent CORS from working
        delete $httpProvider.defaults.headers.common["X-Requested-With"];

        // turn off debug classes from angular for performance boost: 
        // https://code.angularjs.org/1.5.5/docs/guide/production
        $compileProvider.debugInfoEnabled(false);
        $compileProvider.commentDirectivesEnabled(false);
        $compileProvider.cssClassDirectivesEnabled(false);

        // add our http interceptors
        $httpProvider.interceptors.push("securityInterceptor");
        $httpProvider.interceptors.push("cacheBusterInterceptor");

        // $httpProvider.useApplyAsync(true);
        // $httpProvider.interceptors.push("errorInterceptor");

        $provide.decorator("$exceptionHandler", ["$delegate", Token.$Injector.key, ($delegate: ng.IExceptionHandlerService, $injector: ng.auto.IInjectorService) => {
            return ((exception, cause) => {
                $delegate(exception, cause);
                let $telemetryService = $injector.get(Token.TelemetryService.key) as TelemetryService;
                $telemetryService.trackException(exception);
            }) as ng.IExceptionHandlerService;
        }]);

        // Phase 2 (Vue migration): when kbapp runs inside AngularHost (hosted
        // by the Vue SPA), Vue Router owns /, /admin, and every non-embedded
        // URL. Registering these redirects unconditionally causes ui-router
        // to react to benign URL changes (e.g. Vue navigating /products ->
        // /quotes while the embed is about to unmount) and redirect INSIDE
        // the about-to-die AngularHost, which races with Vue's unmount. In
        // hosted mode we keep only the SFDC pre-handler (the /sfdc URL is
        // explicitly an AngularHost route) and skip the rest. In standalone
        // legacy mode (Views/Home/Index.cshtml) all four still register.
        const kbHostedByVue: boolean = !!(window as any).__kbHostedByVue;

        // when we are on the sfdc url, then go to whatever path the sfdc canvas asked for
        $urlRouterProvider.when("/sfdc", [
            Token.$Location.key, Token.$RootScope.key, Token.$State.key, ($location: ng.ILocationService, $rootScope: IRootScope, $state: StateService) => {
                let isSfdcCpq = $rootScope.context.sfdcCanvasRequest.context.environment.parameters.sfdccpq;
                if (isSfdcCpq) {
                    let queryString = $location.search();
                    
                    if (queryString['idtheme']) {
                        return `/configurators/1?idtheme=${queryString['idtheme']}`;
                    }
                    // we use a dummy configurator id here.  The configuratorController will 
                    // communicate through the canvas to get the real id
                    return "/configurators/1";
                } else {
                    let path = $rootScope.context.sfdcCanvasRequest.context.environment.parameters.path;
                    path = path || "/products";
                    return path;
                }
            }]);
        // $urlRouterProvider.when("/sfdc/", handleSfdcUrl);

        if (!kbHostedByVue) {
            // defult to products page
            $urlRouterProvider.when("/", [
                "$location", "$rootScope", ($location: ng.ILocationService, $rootScope: IRootScope) => {
                    if (!$rootScope.company.licenseKbmax && $rootScope.company.license3D) {
                        return "/admin/scenes";
                    }
                    if ($rootScope.companySettings.landingPageUrl && $rootScope.companySettings.landingPageUrl != "") {
                        if ($rootScope.companySettings.landingPageUrl.includes("://")) {
                            window.location.href = $rootScope.companySettings.landingPageUrl;
                        }
                        return $rootScope.companySettings.landingPageUrl;
                    }
                    return "/products";
                }]);

            $urlRouterProvider.when("/admin", [
                "$location", "$rootScope", ($location: ng.ILocationService, $rootScope: IRootScope) => {
                    if ($rootScope.company.licenseKbmax) {
                        if ($rootScope.user.canModifyProducts) {
                            return "/admin/configurators";
                        } else if ($rootScope.user.canModifyScenes && $rootScope.company.license3D) {
                            return "/admin/scenes";
                        } else if ($rootScope.user.canModifyUsers || $rootScope.user.isChannelAdmin) {
                            return "/admin/users";
                        } else if ($rootScope.user.canModifyCurrencies) {
                            return "/admin/currencies";
                        } else if ($rootScope.user.canModifyMedia) {
                            return "/admin/media";
                        }
                    } else {
                        if ($rootScope.company.license3D) {
                            return "/admin/scenes";
                        }
                    }
                    return "/";
                }]);
            $urlRouterProvider.otherwise("/");
        }
        // else: no `.otherwise` is registered, which means unknown URLs inside
        // AngularHost simply don't match any state. That's fine because Vue
        // Router has already matched and routed to an AngularHost component;
        // the only URLs angular sees are configurator/quote-product/scene/sfdc
        // ones that DO match concrete states below.

        // only run animations on elements that have a kb-animate class name.  
        // This should increase performance.See here: https://docs.angularjs.org/api/ng/provider/$animateProvider
        $animateProvider.classNameFilter(/kb-animate/);

        // register the api batch handler
        // httpBatchConfigProvider.setAllowedBatchEndpoint("/api", "/api/batch", {
        //     maxBatchedRequestPerCall: 10,
        //     minimumBatchSize: 2,
        //     batchRequestCollectionDelay: 100,
        //     ignoredVerbs: ["head"],
        //     sendCookies: false,
        //     enabled: true,
        //     // defaults to this value we currently also support a node js multifetch format as well
        //     adapter: "httpBatchAdapter"
        // });

        let stateHelper = new StateHelper($stateProvider, eBundle.app);

        // setup states using ui-router library
        stateHelper.addState({
            name: "kb",
            url: null,
            abstract: true,
            view: Token.PortalView,
            templateName: "portal"
        });
        stateHelper.addState({
            name: "kb.login",
            view: Token.LoginView,
            data: { onLoginPage: true }
        });
        stateHelper.addSearchState({
            name: "kb.products",
            view: Token.ProductsView,
            url: `/products?imagemode&search&searchid`
        });
        // stateHelper.addState({
        //     name: "kb.products",
        //     url: "/products?cat&sort&q&mode",
        //     view: Token.ProductsView
        // });
        stateHelper.addState({
            name: "kb.product",
            url: "/products/:id",
            view: Token.ProductView
        });
        stateHelper.addState({
            name: "kb.productCompare",
            url: "/productcompare/:ids",
            view: Token.ProductCompareView,
            params: {
                ids: { array: true }
            }
        });
        stateHelper.addState({
            name: "kb.configurator",
            url: "/configurators/:id",
            data: { viewType: eViewType.new },
            view: Token.ConfiguratorView
        });
        stateHelper.addState({
            name: "kb.configuredProduct",
            url: "/quoteproducts/:id",
            templateName: "configurator",
            data: { viewType: eViewType.edit },
            view: Token.ConfiguratorView
        });
        stateHelper.addState({
            name: "kb.configuratorTest",
            url: "/configurators/test/:id",
            templateName: "configurator",
            data: { viewType: eViewType.new, isTest: true },
            view: Token.ConfiguratorView
        });
        stateHelper.addCrudStates({
            name: "kb.quote",
            view: Token.QuoteView,
            searchView: Token.QuotesView
        });
        stateHelper.addSearchState({
            name: "kb.configuredProducts",
            view: Token.ConfiguredProductsView
        });
        stateHelper.addCrudStates({
            name: "kb.customer",
            view: Token.CustomerView,
            searchView: Token.CustomersView
        });
        stateHelper.addCrudStates({
            name: "kb.contact",
            view: Token.ContactView,
            searchView: Token.ContactsView
        });
        stateHelper.addState({
            name: "userActivation",
            url: "/users/activate/?idUser&activationGuid&pass&new",
            view: Token.UserActivationView,
            allowAnonymous: true
        });
        stateHelper.addState({
            name: "kb.changePassword",
            url: "/users/changepassword/:id",
            data: { viewType: eViewType.new },
            view: Token.ChangePasswordView
        });
        stateHelper.addState({
            name: "kb.notifications",
            url: "/notifications?q&sort&desc",
            view: Token.NotificationsView
        });
        stateHelper.addState({
            name: "scene",
            url: "/scenes/:id?requestid&&showmove",
            view: Token.ConfiguratorView,
            templateName: "configurator",
            allowAnonymous: true
        });

        $stateProvider.state("kb.admin.**", {
            url: "/admin",
            lazyLoad: ($transition$: Transition) => {
                var bundleLoader = $transition$.injector().get(Token.BundleLoaderService.key) as BundleLoaderService;
                return bundleLoader.load([Token.AdminLib]) as any;
            }
        });
    }]);

kbapp.run([
    Token.AuthService.key,
    Token.$RootScope.key,
    Token.$Http.key,
    Token.PromiseTracker.key,
    Token.$Location.key,
    Token.$State.key,
    Token.$Window.key,
    Token.ThemeService.key,
    Token.TelemetryService.key,
    Token.QuoteService.key,
    Token.ApiService.key,
    Token.SfdcService.key,
    Token.$Transitions.key,
    (
        authService: AuthService,
        $rootScope: IRootScope,
        $http: ng.IHttpService,
        promiseTracker: ng.IPromiseTracker,
        $location: ng.ILocationService,
        $state: StateService,
        $window: ng.IWindowService,
        themeService: ThemeService,
        telemetryService: TelemetryService,
        quoteService: QuoteService,
        apiService: ApiService,
        sfdcService: SfdcService,
        $transitions: TransitionService
    ) => {

        // Phase 2 (Vue migration): hand URL transitions back to Vue Router
        // when the user clicks a link in angular chrome that targets a state
        // Vue owns (home, products, quotes, customers, etc.). Without this
        // bridge the legacy ui-router happily pushes a new URL + fetches a
        // legacy template — but AngularHost is still mounted because Vue
        // Router never saw the URL change, so the user stays inside the
        // embed showing the wrong page.
        //
        // Transitions whose destination state IS an angular-owned view
        // (configurator runtime, quote product, configurator test, scene,
        // and any kb.admin.** state) are left alone so configurator tab
        // navigation inside the embed keeps working.
        //
        // `onBefore({}, ...)` matches every transition. We return `false` to
        // cancel when we redirect to Vue — the URL has already been pushed
        // by Vue Router, so canceling the angular transition leaves things
        // consistent.
        if ((window as any).__kbHostedByVue) {
            const ANGULAR_OWNED_STATE_PREFIXES = [
                "kb.configurator",       // /configurators/:id  (includes configuratorTest, configuredProduct if refactored)
                "kb.configuredProduct",  // /quoteproducts/:id
                "kb.configuratorTest",   // /configurators/test/:id
                "scene",                 // /scenes/:id — public angular scene runtime (AngularHost route in Vue Router); keep here so angular's internal $state.go('scene', ...) stays inside angular rather than bouncing through Vue Router
                "kb.admin",              // /admin/** — lazyLoad state and anything beneath it
                "kb.login",              // legacy login UI — Vue still redirects unauth, but if angular wants to show it, let it.
            ];
            const isAngularOwned = (stateName: string | undefined) => {
                if (!stateName) return false;
                return ANGULAR_OWNED_STATE_PREFIXES.some(p => stateName === p || stateName.startsWith(p + "."));
            };

            $transitions.onBefore({}, (trans: Transition) => {
                const toState = trans.to();
                const toName = toState && toState.name;
                if (isAngularOwned(toName)) return true;

                // Destination is NOT angular's — hand off to Vue Router. The
                // simplest universally-available signal of "what URL does
                // angular want to go to?" is the `url` on the state
                // declaration, but that's parametrized. Use the window
                // location ui-router has already computed via params.
                const router = (window as any).__kbVueRouter;
                if (!router || typeof router.push !== "function") {
                    console.warn(
                        "[app] angular transition to Vue-owned state '" + toName +
                        "' but __kbVueRouter is not available; letting angular handle it"
                    );
                    return true;
                }
                try {
                    // Build the URL from the destination state + params so we
                    // capture query + path segments. `$state.href(toName, toParams)`
                    // returns something like "/products" including the app base.
                    const href = $state.href(toName, trans.params(), { absolute: false });
                    console.info("[app] transition '" + toName + "' -> Vue Router.push(" + href + ")");
                    router.push(href);
                } catch (e) {
                    console.error("[app] failed to bridge transition to Vue Router", e);
                    return true; // fall back to letting angular try
                }
                // Cancel angular's transition: Vue Router now owns navigation.
                return false;
            });
        }

        // store the state service on the rootscope so views can bind to it
        $rootScope.$state = $state;

        $rootScope.baseUrl = "https://" + $(location).attr("host");

        $rootScope.isEpicorEmployee = () => $rootScope.user.email.toLowerCase().endsWith("@kbmax.com") || $rootScope.user.email.toLowerCase().endsWith("@epicor.com");

        // track if we are in sfdc; use sessionStorage instead of cookies
        const getSessionValue = (name: string): string | null => {
            try {
                return sessionStorage.getItem(name);
            } catch (e) {
                console.warn('sessionStorage unavailable', e);
                return null;
            }
        };

        let sfdcCookie = getSessionValue("SfdcCanvasCookie");
        if (sfdcCookie && $rootScope.context.sfdcCanvasRequest) {
            if (JSON.parse(sfdcCookie).issuedAt > $rootScope.context.sfdcCanvasRequest.issuedAt) {
                $rootScope.context.sfdcCanvasRequest = JSON.parse(sfdcCookie);
            }
        }

        if (sfdcCookie && !$rootScope.context.sfdcCanvasRequest) {
            $rootScope.context.sfdcCanvasRequest = JSON.parse(sfdcCookie);
        }
        
        $rootScope.isSfdc = ($rootScope.context.sfdcCanvasRequest != null);
        if ($rootScope.isSfdc) {
            try {
                // store the request as a JSON string in sessionStorage
                sessionStorage.setItem("SfdcCanvasCookie", JSON.stringify($rootScope.context.sfdcCanvasRequest));
            } catch (e) {
                console.warn('Failed to set SfdcCanvasCookie in sessionStorage', e);
            }

            $rootScope.isSfdcCpq = $rootScope.context.sfdcCanvasRequest.context.environment.parameters.sfdccpq;
            let queryString = $location.search();

            $rootScope.isSfdcCustomTheme = false;
            if (queryString["idtheme"]) {
                $rootScope.isSfdcCustomTheme = true;
            }

        }

        // icons array used as a source for some bindings
        $rootScope.icons = Object.values(icons);

        // set initial window size values
        let $win = $(window);
        $rootScope.windowWidth = $win.width();
        $rootScope.windowHeight = $win.height();

        // track the window size
        $(window).resize(() => {
            if (!$rootScope.$$phase) {
                $rootScope.$apply(() => {
                    let $win = $(window);
                    $rootScope.windowWidth = $win.width();
                    $rootScope.windowHeight = $win.height();
                });
            }
        });

        $rootScope.contentTracker = promiseTracker();
        $rootScope.showSpinner = true;
        $rootScope.loginTracker = promiseTracker();
        $rootScope.infiniteScrollTracker = promiseTracker();
        $rootScope.isHoverSupported = !(matchMedia("(hover: none)").matches);
        $rootScope.isIE = /msie\s|trident\//i.test(navigator.userAgent)
        $rootScope.comparedProducts = [];
        $rootScope.recaptchaToken = "6LfFNOAUAAAAAAreoPer8J4F9qh9WLklfpUAbt28";

        // hide browser address bar
        window.scrollTo(0, 1);

        $rootScope.translate = (entity, prop, realProp?) => {
            if (entity) {
                if ($rootScope.clientLanguage != $rootScope.companySettings.defaultLanguage) {
                    if (entity && entity.translations && entity.translations.length) {
                        let translation: IProductTranslation =
                            (entity.translations as ITranslation[])
                                .find(t => t.languageIso == $rootScope.clientLanguage);
                        if (translation) {
                            return translation.data[prop];
                        }
                    }
                }
                return entity[realProp || prop] || entity[prop];
            } else {
                return "";
            }
        };

        $rootScope.supportsFullscreen = !$rootScope.isSfdcCpq && Utils.supportsFullscreen();
        $rootScope.toggleFullscreen = () => {
            if ($rootScope.isSfdc) {
                sfdcService.toggleFullscreen();
            } else {
                Utils.toggleFullscreen();
            }
        };

        $rootScope.setTitle = (subtitle?: string) => {
            const page = subtitle ? `${subtitle} - ` : '';
            const company = $rootScope.environment == eEnvironment.prod ? `${$rootScope.company.name}` : `${$rootScope.company.name} [${$rootScope.environment.toUpperCase()}]`;
            $window.document.title = `${page}${company} - KBMax`;
        };

        $rootScope.companyDatabaseName = `kbmax_${$rootScope.clusterEnv}_${$rootScope.company.uniqueName}_${$rootScope.environment}`;

        // track activity
        let lastMove = Date.now();
        let mouseMoveWait = false;
        window.onmousemove = (event) => {
            if (!mouseMoveWait) {
                lastMove = Date.now();

                mouseMoveWait = true;

                setTimeout(() => {
                    mouseMoveWait = false;
                }, 200);
            }
        };

        const checkTime = 5 * 60000;
        setInterval(() => {
            if (Date.now() - lastMove < checkTime || $rootScope.user.isAnonymous)
                authService.keepAlive();
        }, checkTime);
    }]);
